/* eslint-disable react/prop-types */ // TODO: add props
import { QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import { STATUS as scSTATUS } from 'edrolo/domains/bySchoolClass';
import { EXAM_ANALYTICS_CONSTANTS } from 'edrolo/domains/exams';
import { useProfile } from 'edrolo/domains/profile';
import { Table } from 'edrolo/domains/teacherResultsTable';
import urlResolver from 'edrolo/utils/URLResolver';
import PT from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import IPT from 'react-immutable-proptypes';
import { STATUS } from '../../constants';
import ExportCSVButton from '../ExportCSVButton';
import LayerFilter from '../LayerFilter';
import PrevNextLinks from '../PrevNextLinks';
import { SwitchButton } from '../SwitchButton';
import TitleDetail from '../TitleDetail';
import Legend from './Legend';
import QuestionTableBody from './QuestionTableBody';
import QuestionTableHeader from './QuestionTableHeader';
import style from './SelfReflectionQuestionSetResultsGrid.scss';

// TODO: move this into the questionSetReview domain

const GridHeader = ({
  title,
  classCode,
  classType,
  expirationDate,
  classId,
  examId,
  numQuestions,
  analyticsPayload,
}) => {
  return (
    <div className={style.questionSetGridHeader}>
      <div className={style.row}>
        <h1 className={style.courseHeading}>{title}</h1>
        <PrevNextLinks
          classId={classId}
          questionSetId={examId}
          buildUrl={(questionSetId) =>
            urlResolver.reverse('question-set-grid', {
              classId,
              examId: questionSetId,
            })
          }
          analyticsPayload={analyticsPayload}
        />
      </div>
      <TitleDetail
        classCode={classCode}
        numQuestions={numQuestions}
        classType={classType}
        expirationDate={expirationDate}
      />
    </div>
  );
};

const SelfReflectionQuestionSetResultsGrid = ({
  examId,
  classId,
  exam,
  sections,
  flatQuestions,
  sectionQuestions,
  classDetail,
  classDetailStudents,
  classExamSittings,
  classDetailStatus,
  classExamSittingsStatus,
  loadClassDetail,
  loadClassExamSittings,
  location,
  highlightProfileId,
}) => {
  const { useKeyStore } = useProfile(!!'useImmutable');

  useEffect(() => {
    if (classDetailStatus != scSTATUS.READY) {
      loadClassDetail(classId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [classId]);

  useEffect(() => {
    loadClassExamSittings(classId, examId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [classId, examId]);

  const getQuestionReviewUrl = useCallback(
    (questionId) =>
      urlResolver.reverse('question-set-question-review', {
        classId,
        examId,
        questionId,
      }),
    [classId, examId]
  );

  const [column, setColumn] = useState('name');
  const [asc, setAsc] = useState(true);

  const setSort = (col) => {
    if (col === column) {
      setAsc(!asc);
    } else {
      setColumn(col);
      // you'd expect sorting by responded/complete descend by default
      setAsc(col === 'name' ? true : false);
    }
  };

  const [showCorrectLayer, setShowCorrectLayer] = useKeyStore(
    'grid_show_layer:correct',
    true
  );
  const [showIncorrectLayer, setShowIncorrectLayer] = useKeyStore(
    'grid_show_layer:incorrect',
    true
  );

  if (
    !exam ||
    classDetailStatus !== scSTATUS.READY ||
    (classExamSittingsStatus !== STATUS.READY &&
      classExamSittingsStatus !== STATUS.RELOADING)
  ) {
    return <LoadingIndicator loadingText="Loading Class Data" />;
  }

  // Figure out flatquestion index after which to add a SpacerCell.
  // I'm sure there could be a better way of doing this.
  const indicesToAddSpacerCellTo = [];
  let runningTotal = -1;
  sections.forEach((section) => {
    runningTotal += sectionQuestions.get(`${section.get('id')}`).size;
    indicesToAddSpacerCellTo.push(runningTotal);
  });

  const multiChoiceResponses = classExamSittings
    .flatMap((classExamSitting) => classExamSitting.get('responses'))
    .filter(
      (response) =>
        (response.get('your_answer') &&
          response.get('question_type') ===
            QUESTION_TYPES.SINGLE_ANSWER_MULTI_CHOICE) ||
        response.get('question_type') ===
          QUESTION_TYPES.MULTI_ANSWER_MULTI_CHOICE
    );

  const isCorrectCount = multiChoiceResponses.filter((response) =>
    response.get('is_correct')
  ).size;

  const isIncorrectCount = multiChoiceResponses.filter(
    (response) => !response.get('is_correct')
  ).size;

  const analyticsPayload = {
    origin: EXAM_ANALYTICS_CONSTANTS.ORIGIN.EXAMS,
    page: EXAM_ANALYTICS_CONSTANTS.PAGES.QUESTION_SET_RESULTS_GRID,
    classId: classId,
    lessonId: null,
    examId: examId,
  };

  const csvExportUrl = urlResolver.reverse('api:get:question-set-csv-export', {
    classId,
    examId,
  });

  const title = exam.get('title');
  const numQuestions = flatQuestions.size;
  const navigatedFrom = {
    url: location,
    label: `${numQuestions} questions - ${title}`,
  };

  return (
    <>
      <GridHeader
        title={title}
        classCode={classDetail.get('title')}
        classType={classDetail.get('class_type')}
        expirationDate={classDetail.get('expiration_date')}
        examId={examId}
        classId={classId}
        numQuestions={numQuestions}
        analyticsPayload={analyticsPayload}
      />
      <div
        className={style.flexRow}
        data-test={'selfReflectionQuestionSetResults'}
      >
        <div>
          <h3 className={style.heading}>All questions</h3>
        </div>

        <div className={`${style.flexRowReverse} ${style.subHeaderMargin}`}>
          <SwitchButton
            allQuestionsUrl={urlResolver.reverse('question-set-index', {
              examId,
              classId,
            })}
            analyticsPayload={analyticsPayload}
          />
          <LayerFilter
            showCorrectLayer={showCorrectLayer}
            setShowCorrectLayer={setShowCorrectLayer}
            showIncorrectLayer={showIncorrectLayer}
            setShowIncorrectLayer={setShowIncorrectLayer}
            correctMCQresponseCount={isCorrectCount}
            incorrectMCQresponseCount={isIncorrectCount}
            analyticsPayload={analyticsPayload}
          />
          <ExportCSVButton
            csvExportUrl={csvExportUrl}
            onClick={() => {
              global.analytics.track('exported question set data as csv', {
                ...analyticsPayload,
              });
            }}
          />
        </div>
      </div>

      <Legend />

      <Table className={style.table}>
        <QuestionTableHeader
          examId={examId}
          sections={sections}
          flatQuestions={flatQuestions}
          sectionQuestions={sectionQuestions}
          getQuestionReviewUrl={getQuestionReviewUrl}
          onSelectSort={setSort}
          sortColumn={column}
          sortAscending={asc}
          indicesToAddSpacerCellTo={indicesToAddSpacerCellTo}
          analyticsPayload={analyticsPayload}
        />
        <QuestionTableBody
          students={classDetailStudents}
          flatQuestions={flatQuestions}
          classExamSittings={classExamSittings}
          getQuestionReviewUrl={getQuestionReviewUrl}
          sortColumn={column}
          sortAscending={asc}
          indicesToAddSpacerCellTo={indicesToAddSpacerCellTo}
          showCorrectLayer={showCorrectLayer}
          showIncorrectLayer={showIncorrectLayer}
          classId={classId}
          navigatedFrom={navigatedFrom}
          analyticsPayload={analyticsPayload}
          highlightProfileId={highlightProfileId}
        />
      </Table>
    </>
  );
};

SelfReflectionQuestionSetResultsGrid.propTypes = {
  examId: PT.oneOfType([PT.string, PT.number]).isRequired,
  classId: PT.oneOfType([PT.string, PT.number]).isRequired,
  exam: IPT.map.isRequired,
  classDetail: IPT.map,
  classExamSittings: IPT.list,
  classDetailStatus: PT.string,
  classExamSittingsStatus: PT.string,
  loadClassDetail: PT.func.isRequired,
  loadClassExamSittings: PT.func.isRequired,
};

export default SelfReflectionQuestionSetResultsGrid;
